/**
 * New Create Task Modal (shown when ff_task_ui_taskspage === true).
 * Brand-new components for the new design; existing CreateTask is unchanged when flag is false.
 * Reuses i18n from notification.createTask where text matches; no changes to Send Email components.
 */
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Box, Button, Checkbox, CircularProgress, FormControlLabel, Grid, IconButton, MenuItem, TextField, Typography } from '@mui/material';
import DeleteOutlinedIcon from '@mui/icons-material/DeleteOutlined';
import { useTranslation } from 'react-i18next';
import { addDays, addHours, addYears, isValid, isWithinInterval, parse, parseISO } from 'date-fns';
import { useDispatch, useSelector } from 'react-redux';
import { GenericDialog, IGenericDialogButton } from 'app/components/Alerts/GenericDialog';
import { globalActions } from 'app/ApplicationRoot/Global.redux';
import { Cancel } from 'app/components/Common/CancelModal/Cancel';
import CloudUploadOutlinedIcon from '@mui/icons-material/CloudUploadOutlined';
import { DateRangePicker } from 'app/ComponentLibrary/DateRangeNew';
import { FormatDate } from 'app/components/Notification/Tasks/Common/FormatData';
import { getAttachmentIcon } from 'app/components/Notification/Common/AttachmentFileTypes';
import { CreateTaskNewStyles } from './CreateTaskModalNew.styles';
import { FromField, IFromOption } from './FromField';
import { ToField, IToRecipient } from './ToField';
import { TaskEntity } from 'app/models/Tasks/Tasks';
import { selectUser } from 'oidc/user.selectors';
import { fetchplacementEmpData, formatEmployeeName } from 'app/components/Tasks/CreateTask/FormatData';
import { taskDetailsActions } from 'app/components/Tasks/store/Tasks.redux';
import {
  selectIsNewTaskCategoriesLoading,
  selectNewTaskCategories,
  selectNewTaskCreateError,
  selectNewTaskCreatedId,
} from 'app/components/Tasks/store/Tasks.selectors';
import { buildCreateTaskRequestFromNewModal } from './buildCreateTaskRequest';
import { getValidatedAttachments } from './attachmentValidation';

export interface CreateTaskModalNewProps {
  open: boolean;
  handleClose: () => void;
  getAllIds: any;
  container: number;
  recruiterDetails?: any;
}

const getInitialFormState = () => ({
  subject: '',
  description: '',
  category: '',
  followUpDate: '',
  priority: 'Normal',
  flagChecked: false,
  optionApprovalChecked: false,
  followUpChecked: false,
  attachmentsLength: 0,
});

export const CreateTaskModalNew = ({
  open,
  handleClose,
  getAllIds,
  container,
  recruiterDetails,
}: CreateTaskModalNewProps) => {
  const { t } = useTranslation();
  const { classes, cx } = CreateTaskNewStyles();
  const dispatch = useDispatch();
  const [expanded, setExpanded] = useState(false);
  const [discardModalOpen, setDiscardModalOpen] = useState(false);
  const [subject, setSubject] = useState('');
  const [description, setDescription] = useState('');
  const [category, setCategory] = useState('');
  const [followUpDate, setFollowUpDate] = useState('');
  const [priority, setPriority] = useState('Normal');
  const [flagChecked, setFlagChecked] = useState(false);
  const [optionApprovalChecked, setOptionApprovalChecked] = useState(false);
  const [followUpChecked, setFollowUpChecked] = useState(false);
  const [followUpDateError, setFollowUpDateError] = useState<string | null>(null);
  const [attachments, setAttachments] = useState<File[]>([]);
  const [hasEdits, setHasEdits] = useState(false);
  const [isCreateRequested, setIsCreateRequested] = useState(false);
  const [selectedFromOption, setSelectedFromOption] = useState<IFromOption | null>(null);
  const [toRecipients, setToRecipients] = useState<IToRecipient[]>([]);
  const [ccRecipients, setCcRecipients] = useState<IToRecipient[]>([]);
  const [placementEmployees, setPlacementEmployees] = useState<IToRecipient[]>([]);
  const userInfo = useSelector(selectUser);
  const newTaskCategories = useSelector(selectNewTaskCategories);
  const isNewTaskCategoriesLoading = useSelector(selectIsNewTaskCategoriesLoading);
  const newTaskCreatedId = useSelector(selectNewTaskCreatedId);
  const newTaskCreateError = useSelector(selectNewTaskCreateError);

  const sortedNewTaskCategories = useMemo(
    () =>
      (newTaskCategories || [])
        .slice()
        .sort((a: any, b: any) => {
          const nameA = (a?.name ?? a?.description ?? a?.value ?? '').toString().toLowerCase();
          const nameB = (b?.name ?? b?.description ?? b?.value ?? '').toString().toLowerCase();
          return nameA.localeCompare(nameB);
        }),
    [newTaskCategories],
  );

  /** Current user as From option and To recipient for Follow-Up checkbox behavior */
  const currentUserFromOption = useMemo((): IFromOption | null => {
    if (userInfo?.userInfo?.employeeId == null) return null;
    return {
      id: userInfo.userInfo.employeeId,
      coveredEmployeeId: userInfo.userInfo.employeeId,
      coveredEmployeeFirstName: userInfo.userInfo.firstName ?? '',
      coveredEmployeeLastName: userInfo.userInfo.lastName ?? '',
      title: '-',
    };
  }, [userInfo?.userInfo?.employeeId, userInfo?.userInfo?.firstName, userInfo?.userInfo?.lastName]);

  const currentUserToRecipient = useMemo((): IToRecipient | null => {
    if (userInfo?.userInfo?.employeeId == null) return null;
    const label =
      formatEmployeeName(userInfo.userInfo.firstName ?? '', userInfo.userInfo.lastName ?? '').trim() || 'Me';
    return {
      object: { empId: userInfo.userInfo.employeeId, label, role: '-' },
      label,
    };
  }, [userInfo?.userInfo?.employeeId, userInfo?.userInfo?.firstName, userInfo?.userInfo?.lastName]);

  // Fetch Key Contacts / Employee list when modal opens from Placement context
  useEffect(() => {
    if (open && container === TaskEntity.PLACEMENTS && getAllIds?.placementId) {
      const placementId = getAllIds.placementId?.toString?.() ?? String(getAllIds.placementId);
      if (placementId && Number(placementId) > 0) {
        fetchplacementEmpData({ header: { placementId: Number(placementId) } } as any, setPlacementEmployees);
      }
    } else if (open) {
      setPlacementEmployees([]);
    }
  }, [open, container, getAllIds?.placementId]);

  /** Follow-Up checked: set From to current user; To to [current user] and disabled; CC clear and disabled; Option Approval clear and disabled */
  useEffect(() => {
    if (!followUpChecked) return;
    if (currentUserFromOption != null) {
      setSelectedFromOption(currentUserFromOption);
    }
    if (currentUserToRecipient != null) {
      setToRecipients([currentUserToRecipient]);
    }
    setCcRecipients([]);
    setOptionApprovalChecked(false);
  }, [followUpChecked, currentUserFromOption, currentUserToRecipient]);

  // Reset form and edit state when modal opens (fresh form on each open)
  useEffect(() => {
    if (open) {
      dispatch(taskDetailsActions.setNewTaskCreatedId(undefined));
      dispatch(taskDetailsActions.setNewTaskCreateError(false));
      const initial = getInitialFormState();
      const date = new Date();
      date.setHours(23, 59, 59, 999);
      const defaultFollowUpDate = FormatDate(date);
      setSubject(initial.subject);
      setDescription(initial.description);
      setCategory(initial.category);
      setFollowUpDate(defaultFollowUpDate);
      setPriority(initial.priority);
      setFlagChecked(initial.flagChecked);
      setOptionApprovalChecked(initial.optionApprovalChecked);
      setFollowUpChecked(initial.followUpChecked);
      setAttachments([]);
      setHasEdits(false);
      setSelectedFromOption(currentUserFromOption ?? null);
      setToRecipients([]);
      setCcRecipients([]);
      setFollowUpDateError(null);
      setIsCreateRequested(false);
    }
  }, [open, currentUserFromOption]);

  // Fetch task categories for the new Create Task modal when opened
  useEffect(() => {
    if (open && (!newTaskCategories || newTaskCategories.length === 0)) {
      dispatch(taskDetailsActions.getNewTaskCategoriesAction());
    }
  }, [open, newTaskCategories, dispatch]);

  // Set default category (- General -) once categories are loaded
  useEffect(() => {
    if (!open || !newTaskCategories || newTaskCategories.length === 0 || category) return;
    const generalCategory =
      newTaskCategories.find(
        (c: any) =>
          c?.name === '- General -' ||
          c?.value === '- General -' ||
          (typeof c?.name === 'string' && c.name.toLowerCase() === 'general'),
      ) || newTaskCategories[0];
    if (generalCategory) {
      const id = generalCategory.id ?? generalCategory.value ?? '';
      if (id !== '') {
        setCategory(String(id));
      }
    }
  }, [open, newTaskCategories, category]);

  const markAsEdited = () => setHasEdits(true);

  const showAttachmentError = (message: string) => {
    dispatch(globalActions.setSnackBar({ message, severity: 'error' }));
  };

  const handleAddAttachments = (newFiles: File[]) => {
    const nextAttachments = getValidatedAttachments({
      existingAttachments: attachments,
      newFiles,
      t,
      onError: showAttachmentError,
    });
    if (nextAttachments.length !== attachments.length) {
      setAttachments(nextAttachments);
      markAsEdited();
    }
  };

  const validateFollowUpDate = (value: string): string | null => {
    const trimmed = (value || '').trim();

    if (!trimmed) {
      return t('required');
    }

    if (trimmed.includes('_') || trimmed.includes(' ')) {
      return t('invalidFormat');
    }

    const now = new Date();
    const isoDate = parseISO(trimmed);
    const isoValid = isValid(isoDate);

    if (isoValid) {
      const withinInterval = isWithinInterval(isoDate, {
        start: addDays(now, -1),
        end: addYears(now, 10),
      });
      if (withinInterval) {
        return null;
      }
      return t('invalidDate');
    }

    const customDate = parse(trimmed, 'MM/dd/yyyy', new Date());
    const customValid = isValid(customDate);

    if (
      customValid &&
      isWithinInterval(customDate, {
        start: addDays(now, -1),
        end: addYears(now, 10),
      })
    ) {
      return null;
    }

    return t('invalidDate');
  };

  // Required-field validations for enabling Save.
  const isToValid = (toRecipients?.length ?? 0) > 0;
  const isFollowUpDateValid = !!followUpDate && !followUpDateError;
  const isCategoryValid = category != null && String(category).trim().length > 0;
  const isPriorityValid = !!priority;
  const isSubjectValid = subject.trim().length > 0;

  const isSaveEnabled =
    hasEdits && isToValid && isFollowUpDateValid && isCategoryValid && isPriorityValid && isSubjectValid;

  const onCloseModal = (_e: React.MouseEvent, reason?: string) => {
    if (reason === 'backdropClick') return;
    if (hasEdits) {
      setDiscardModalOpen(true);
    } else {
      handleClose();
    }
  };

  const handleDiscardConfirm = () => {
    setDiscardModalOpen(false);
    handleClose();
  };

  const handleSave = () => {
    if (!isSaveEnabled) return;
    const request = buildCreateTaskRequestFromNewModal({
      subject,
      description,
      category,
      followUpDate,
      priority,
      flagChecked,
      optionApprovalChecked,
      followUpChecked,
      toRecipients,
      ccRecipients,
      selectedFromOption,
      getAllIds: getAllIds || {},
      container,
    });
    dispatch(taskDetailsActions.setNewTaskCreateError(false));
    dispatch(
      taskDetailsActions.saveNewTaskRequest({
        request,
        attachments: attachments || [],
      }),
    );
    setIsCreateRequested(true);
  };

  // Close modal when v1 create task succeeds (saga sets newTaskCreatedId)
  useEffect(() => {
    if (open && isCreateRequested && newTaskCreatedId != null && Number(newTaskCreatedId) > 0) {
      dispatch(taskDetailsActions.setNewTaskCreatedId(undefined));
      setIsCreateRequested(false);
      handleClose();
    }
  }, [open, isCreateRequested, newTaskCreatedId, dispatch, handleClose]);

  // Re-enable Save after a failed create so the user is not stuck with a disabled button
  useEffect(() => {
    if (newTaskCreateError && isCreateRequested) {
      setIsCreateRequested(false);
    }
  }, [newTaskCreateError, isCreateRequested]);

  const handleFromChange = (option: IFromOption) => {
    const prevId = selectedFromOption?.id ?? option.id;
    if (option.id !== prevId) setHasEdits(true);
    setSelectedFromOption(option);
  };

  const renderSaveDialogButton = useCallback(
    (btnProps: IGenericDialogButton) => {
      const {
        text: label = '',
        loading,
        renderer: _renderer,
        useDialogOnCloseEvent: _useDialogOnCloseEvent,
        tooltipProps: _tooltipProps,
        variant = 'contained',
        color = 'primary',
        size = 'large',
        disableElevation = true,
        ...buttonRest
      } = btnProps;

      return (
        <Button
          disableRipple
          disableFocusRipple
          variant={variant}
          color={color}
          size={size}
          disableElevation={disableElevation}
          data-testid="generic-dialog-button-id"
          className={classes.saveDialogPrimaryAction}
          {...buttonRest}
        >
          <span
            className={cx(classes.saveDialogButtonLabelWrap, loading && classes.saveDialogButtonLabelWrapWhenLoading)}
          >
            <span className={loading ? classes.saveDialogButtonTextHidden : undefined}>{label}</span>
            {loading ? (
              <span className={classes.saveDialogButtonSpinnerOverlay} aria-hidden>
                <CircularProgress size={20} color="inherit" aria-label={t('common.loading')} />
              </span>
            ) : null}
          </span>
        </Button>
      );
    },
    [classes, t],
  );

  return (
    <>
      <GenericDialog
        open={open}
        fullWidth
        maxWidth="xl"
        variant="blue"
        draggable
        className={classes.modalContainer}
        onClose={onCloseModal}
        dialogTitleProps={{
          text: t('notification.createTask.modalTitle'),
          closeButton: true,
          expandable: true,
          onExpandChange: setExpanded,
        }}
        dialogContentProps={{
          classes: { root: classes.dialogContent },
        }}
        dialogActions={[
          {
            text: t('notification.createTask.cancelBtn').toUpperCase(),
            variant: 'contained',
            color: 'tertiary',
            onClick: e => onCloseModal(e as unknown as React.MouseEvent),
          },
          {
            text: t('notification.createTask.saveBtn').toUpperCase(),
            variant: 'contained',
            color: 'primary',
            disabled: !isSaveEnabled || isCreateRequested,
            loading: isCreateRequested,
            onClick: handleSave,
            renderer: renderSaveDialogButton,
          },
        ]}
      >
        <Grid container spacing={2} className={classes.contentGrid} data-testid="create-task-modal-new">
        {/* From / To / CC - label and value on same line */}
        <Grid item xs={12} className={classes.section}>
          <Box className={classes.fromToCcRow}>
            <Typography variant="body2" component="span" className={classes.fromToCcLabel}>
              {t('notification.createTask.newDesign.fromLabel')}
            </Typography>
            <Box className={classes.fromToCcValue}>
              <FromField
                value={selectedFromOption}
                onChange={handleFromChange}
                displayClassName={classes.fromToRow}
              />
            </Box>
          </Box>
        </Grid>
        <Grid item xs={12} className={classes.section}>
          <Box className={classes.fromToCcRow}>
            <Typography variant="body2" component="span" className={classes.fromToCcLabel}>
              {t('notification.createTask.newDesign.toLabel')}
            </Typography>
            <Box className={classes.fromToCcValue}>
              <ToField
                value={toRecipients}
                onChange={recipients => {
                  setToRecipients(recipients);
                  markAsEdited();
                }}
                placementEmployees={placementEmployees}
                container={container}
                excludeEmpIds={ccRecipients.map(r => r?.object?.empId).filter((id): id is number => id != null)}
                displayClassName={classes.fromToRow}
                placeholder={t('notification.createTask.newDesign.toPlaceholder')}
                disabled={followUpChecked}
              />
            </Box>
          </Box>
        </Grid>
        <Grid item xs={12} className={classes.section}>
          <Box className={classes.fromToCcRow}>
            <Typography variant="body2" component="span" className={classes.fromToCcLabel}>
              {t('notification.createTask.ccBtn')}
            </Typography>
            <Box className={classes.fromToCcValue}>
              <ToField
                value={ccRecipients}
                onChange={recipients => {
                  setCcRecipients(recipients);
                  markAsEdited();
                }}
                placementEmployees={placementEmployees}
                container={container}
                excludeEmpIds={toRecipients.map(r => r?.object?.empId).filter((id): id is number => id != null)}
                placeholder={t('notification.createTask.newDesign.ccPlaceholder')}
                disabled={followUpChecked}
              />
            </Box>
          </Box>
        </Grid>

        {/* Subject */}
        <Grid item xs={12} className={classes.section}>
          <Box className={classes.fromToCcRow}>
            <Typography variant="body2" component="span" className={classes.fromToCcLabel}>
              {t('notification.createTask.newDesign.subjectLabel')}
            </Typography>
            <Box className={classes.fromToCcValue}>
              <Box className={classes.fromToRow}>
                <TextField
                  fullWidth
                  variant="standard"
                  placeholder={t('notification.createTask.newDesign.subjectPlaceholder')}
                  value={subject}
                  onChange={e => {
                    setSubject(e.target.value);
                    markAsEdited();
                  }}
                  className={classes.subjectInputRow}
                  InputProps={{
                    disableUnderline: true,
                  }}
                />
              </Box>
            </Box>
          </Box>
        </Grid>

        {/* Category, Follow-Up Date, Priority, checkboxes - single line */}
        <Grid item xs={12} className={classes.section}>
          <Box className={classes.categoryPriorityCheckboxRow}>
            <TextField
              className={classes.categoryPriorityInput}
              variant="filled"
              label={t('notification.createTask.newDesign.categoryLabel')}
              InputLabelProps={{ className: classes.inputLabel }}
              select
              SelectProps={{ displayEmpty: true }}
              value={category}
              onChange={e => {
                const nextCategory = e.target.value;
                setCategory(nextCategory);

                // If subject is blank, default it to the selected category's display name.
                if (!subject.trim() && newTaskCategories && newTaskCategories.length > 0) {
                  const match = newTaskCategories.find((option: any) => {
                    const id = option.id ?? option.value;
                    return String(id) === String(nextCategory);
                  });
                  if (match) {
                    const name = match.name ?? match.description ?? match.value;
                    if (name) {
                      setSubject(name);
                    }
                  }
                }

                markAsEdited();
              }}
            >
              {isNewTaskCategoriesLoading && (
                <MenuItem value="" disabled>
                  {t('common.loading')}
                </MenuItem>
              )}
              {!isNewTaskCategoriesLoading &&
                sortedNewTaskCategories.map((option: any) => {
                  const id = option.id ?? option.value;
                  const name = option.name ?? option.description ?? option.value;
                  if (!id || !name) return null;
                  return (
                    <MenuItem key={id} value={String(id)}>
                      {name}
                    </MenuItem>
                  );
                })}
            </TextField>
            <Box className={classes.followUpDatePickerWrapper}>
              <DateRangePicker
                variant="filled"
                placeholder={t('notification.createTask.newDesign.followUpDateLabel')}
                secondaryLabel={t('notification.createTask.newDesign.followUpDateLabel')}
                useMaxWidth
                single
                value={followUpDate}
                initialDateRange={{
                  startDate: followUpDate ? new Date(followUpDate) : undefined,
                  endDate: followUpDate ? new Date(followUpDate) : undefined,
                  useLabelAsValue: true,
                }}
                labelClass={classes.followUpDateLabel}
                minDate={addDays(new Date(), -1)}
                maxWidth={160}
                trailingIconShow
                isDisabled={false}
                disablePortal
                wrapperClass={classes.followUpDateCalendar}
                compactHeader
                actionBar={false}
                clickAway
                keepClickAwayVal
                onChange={e => {
                  const next = e?.endDate ? FormatDate(e.endDate.setHours(18)) : '';
                  setFollowUpDate(next);
                  setFollowUpDateError(validateFollowUpDate(next));
                  markAsEdited();
                }}
                onBlurText={dateString => {
                  let nextValue = dateString ?? '';
                  if (dateString && dateString.length > 0 && !dateString.includes('_')) {
                    try {
                      const parsedDate = parse(dateString, 'MM/dd/yyyy', new Date());
                      const midDayValue = addHours(parsedDate, 18);
                      nextValue = FormatDate(midDayValue);
                    } catch {
                      nextValue = dateString;
                    }
                  }
                  setFollowUpDate(nextValue);
                  setFollowUpDateError(validateFollowUpDate(nextValue));
                  if (nextValue) {
                    markAsEdited();
                  }
                }}
                definedRanges={[
                  {
                    label: t('notification.createTask.dueDateLabels.today'),
                    startDate: new Date(),
                    endDate: new Date(),
                    useLabelAsValue: false,
                  },
                  {
                    label: t('notification.createTask.dueDateLabels.tomorrow'),
                    startDate: addDays(new Date(), 1),
                    endDate: addDays(new Date(), 1),
                    useLabelAsValue: false,
                  },
                  {
                    label: t('notification.createTask.dueDateLabels.7Days'),
                    startDate: addDays(new Date(), 7),
                    endDate: addDays(new Date(), 7),
                    useLabelAsValue: false,
                  },
                  {
                    label: t('notification.createTask.dueDateLabels.30Days'),
                    startDate: addDays(new Date(), 30),
                    endDate: addDays(new Date(), 30),
                    useLabelAsValue: false,
                  },
                ]}
                error={!!followUpDateError}
                helperText={followUpDateError || ''}
              />
            </Box>
            <TextField
              className={classes.categoryPriorityInput}
              variant="filled"
              label={t('notification.createTask.priority')}
              placeholder={t('notification.createTask.newDesign.priorityPlaceholder')}
              InputLabelProps={{ className: classes.inputLabel }}
              select
              SelectProps={{ native: true, displayEmpty: true }}
              value={priority}
              onChange={e => {
                setPriority(e.target.value);
                markAsEdited();
              }}
            >
              <option value="High">{t('notification.createTask.newDesign.priorityHigh')}</option>
              <option value="Low">{t('notification.createTask.newDesign.priorityLow')}</option>
              <option value="Normal">{t('notification.createTask.newDesign.priorityNormal')}</option>
            </TextField>
            <Box className={classes.checkboxGroup}>
              <FormControlLabel
                control={
                  <Checkbox
                    color="primary"
                    checked={flagChecked}
                    onChange={e => {
                      setFlagChecked(e.target.checked);
                      markAsEdited();
                    }}
                  />
                }
                label={t('notification.createTask.newDesign.flagLabel')}
                className={classes.checkboxLabel}
              />
              <FormControlLabel
                control={
                  <Checkbox
                    color="primary"
                    checked={optionApprovalChecked}
                    onChange={e => {
                      setOptionApprovalChecked(e.target.checked);
                      markAsEdited();
                    }}
                    disabled={followUpChecked}
                  />
                }
                label={t('notification.createTask.newDesign.optionApprovalLabel')}
                className={classes.checkboxLabel}
              />
              <FormControlLabel
                control={
                  <Checkbox
                    color="primary"
                    checked={followUpChecked}
                    onChange={e => {
                      setFollowUpChecked(e.target.checked);
                      markAsEdited();
                    }}
                  />
                }
                label={t('notification.createTask.newDesign.followUpLabel')}
                className={classes.checkboxLabel}
              />
            </Box>
          </Box>
        </Grid>

        {/* Message - no label, open text, 10 lines with scroll when exceeded, border top */}
        <Grid item xs={12} className={`${classes.section} ${classes.messageSection}`}>
          <TextField
            fullWidth
            multiline
            minRows={10}
            maxRows={10}
            variant="filled"
            value={description}
            onChange={e => {
              setDescription(e.target.value);
              markAsEdited();
            }}
            className={classes.messageInput}
          />
        </Grid>

        {/* Attachments */}
        <Grid item xs={12} className={classes.section}>
          <Grid container spacing={2} alignItems="flex-start">
            <Grid item xs={12} md={5}>
              <Box
                component="label"
                htmlFor="create-task-new-attachment"
                className={classes.dropzone}
                onDragOver={e => e.preventDefault()}
                onDrop={e => {
                  e.preventDefault();
                  const files = Array.from(e.dataTransfer.files);
                  handleAddAttachments(files);
                }}
              >
                <CloudUploadOutlinedIcon className={classes.dropzoneIcon} />
                <Typography variant="body2" className={classes.dropzoneText} component="span">
                  {t('notification.createTask.newDesign.attachmentDropzoneText')}
                </Typography>
                <input
                  type="file"
                  multiple
                  hidden
                  id="create-task-new-attachment"
                  accept=".jpeg,.jpg,.png,.docx,.doc,.xlsx,.xls,.pdf,.csv,.msg"
                  onChange={e => {
                    const files = e.target.files ? Array.from(e.target.files) : [];
                    handleAddAttachments(files);
                    e.target.value = '';
                  }}
                />
              </Box>
            </Grid>
            <Grid item xs={12} md={7}>
              {attachments.length > 0 && (
                <Box className={classes.attachmentList}>
                  {attachments.map((file, index) => (
                    <Box key={`${file.name}-${index}`} className={classes.attachmentItem}>
                      <img
                        src={getAttachmentIcon(file.name.split('.').pop()?.toLowerCase())}
                        alt=""
                        className={classes.attachmentFileIcon}
                      />
                      <Typography variant="body2" noWrap className={classes.attachmentName}>
                        {file.name}
                      </Typography>
                      <IconButton
                        size="small"
                        aria-label={t('notification.createTask.delete')}
                        onClick={() => {
                          markAsEdited();
                          setAttachments(prev => prev.filter((_, i) => i !== index));
                        }}
                        className={classes.attachmentRemoveBtn}
                      >
                        <DeleteOutlinedIcon fontSize="small" />
                      </IconButton>
                    </Box>
                  ))}
                </Box>
              )}
            </Grid>
          </Grid>
        </Grid>
        </Grid>
      </GenericDialog>
      <Cancel
        openDialog={discardModalOpen}
        handleConfirmAction={handleDiscardConfirm}
        handleCancelAction={() => setDiscardModalOpen(false)}
      />
    </>
  );
};
